import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Volume2, 
  VolumeX, 
  Play, 
  Lock, 
  Flame, 
  Zap, 
  MessageCircle,
  Instagram,
  Crown,
  Star,
  ChevronDown,
  Check,
  X
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Slider } from "./components/ui/slider";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2cc5740-69ad-41f0-b94d-71f5d69a20ba/artifacts/0pbpfr6u_logo%20rocco%20sifredi.jpeg";
const AUDIO_URL = "https://customer-assets.emergentagent.com/job_f2cc5740-69ad-41f0-b94d-71f5d69a20ba/artifacts/9gylp8gx_All%20The%20Time%20%5BA4DQ6UyeIKU%5D%20%28mp3cut.net%29.mp3";
const HERO_BG = "https://images.unsplash.com/photo-1699570638045-7d3e736db619?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxSaW8lMjBkZSUyMEphbmVpcm8lMjBuaWdodCUyMGNpdHklMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzczNjA3ODQ4fDA&ixlib=rb-4.1.0&q=85";

const WHATSAPP_NUMBER = "5521972232170";
const INSTAGRAM_HANDLE = "jonatasbeirao";

// WhatsApp redirect helper
const openWhatsApp = (message) => {
  const encodedMessage = encodeURIComponent(message);
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
};

// Placeholder thumbnails for content preview
const PREVIEW_CONTENT = [
  { id: 1, title: "Carioca Quente", duration: "2:30", premium: false },
  { id: 2, title: "Noite no Rio", duration: "3:15", premium: false },
  { id: 3, title: "Praia Secreta", duration: "4:20", premium: false },
  { id: 4, title: "Lapa Proibida", duration: "5:00", premium: false },
  { id: 5, title: "Copacabana VIP", duration: "6:45", premium: false },
  { id: 6, title: "Suíte Ipanema", duration: "8:30", premium: true },
  { id: 7, title: "Especial Botafogo", duration: "10:00", premium: true },
  { id: 8, title: "Madrugada Carioca", duration: "12:00", premium: true },
];

// Audio Player Component - Auto plays on scroll
const AudioPlayer = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState([50]);
  const [showVolume, setShowVolume] = useState(false);
  const hasTriedAutoplay = useRef(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume[0] / 100;
    }
  }, [volume]);

  // Auto-play on first scroll
  useEffect(() => {
    const handleScroll = async () => {
      if (!hasTriedAutoplay.current && audioRef.current) {
        hasTriedAutoplay.current = true;
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (e) {
          console.log("Autoplay on scroll blocked");
        }
      }
    };

    const handleClick = async () => {
      if (!hasTriedAutoplay.current && audioRef.current) {
        hasTriedAutoplay.current = true;
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (e) {
          console.log("Autoplay on click blocked");
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { once: true });
    window.addEventListener('click', handleClick, { once: true });
    window.addEventListener('touchstart', handleClick, { once: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('touchstart', handleClick);
    };
  }, []);

  const togglePlay = async () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        try {
          await audioRef.current.play();
        } catch (e) {
          console.log("Autoplay blocked, user interaction needed");
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1 }}
      className="fixed bottom-6 left-6 z-50 flex items-center gap-3"
      onMouseEnter={() => setShowVolume(true)}
      onMouseLeave={() => setShowVolume(false)}
    >
      <audio ref={audioRef} src={AUDIO_URL} loop />
      
      <Button
        data-testid="audio-toggle-btn"
        onClick={togglePlay}
        className="w-12 h-12 rounded-full bg-[#0a0a0a]/90 backdrop-blur-md border border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#0a0a0a] transition-all duration-300"
        variant="ghost"
      >
        {isPlaying ? (
          <Volume2 className="w-5 h-5 text-[#D4AF37]" />
        ) : (
          <VolumeX className="w-5 h-5 text-[#a1a1aa]" />
        )}
      </Button>

      <AnimatePresence>
        {showVolume && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 100 }}
            exit={{ opacity: 0, width: 0 }}
            className="overflow-hidden"
          >
            <Slider
              data-testid="volume-slider"
              value={volume}
              onValueChange={setVolume}
              max={100}
              step={1}
              className="w-24"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => (
  <motion.button
    data-testid="whatsapp-floating-btn"
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 1.5, type: "spring" }}
    onClick={() => openWhatsApp("Olá! Vim pelo site e quero saber mais sobre o conteúdo premium!")}
    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#22c55e] flex items-center justify-center pulse-whatsapp hover:scale-110 transition-transform duration-300"
  >
    <MessageCircle className="w-7 h-7 text-white fill-white" />
  </motion.button>
);

// Hero Section
const HeroSection = () => {
  const scrollToContent = () => {
    document.getElementById('content-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img 
          src={HERO_BG} 
          alt="Rio de Janeiro Night" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#050505]" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Logo */}
          <motion.img
            src={LOGO_URL}
            alt="Rocco Sifredi"
            className="w-72 md:w-96 mx-auto mb-8 drop-shadow-2xl"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          />

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-[#a1a1aa] mb-4 tracking-wide"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
          >
            O melhor conteúdo adulto carioca
          </motion.p>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6 glitch-text"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            <span className="gold-gradient-text">Exclusivo.</span>{" "}
            <span className="text-[#FF0033]">Picante.</span>{" "}
            <span className="text-white">Carioca.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-[#a1a1aa] text-base md:text-lg mb-10 max-w-xl mx-auto"
          >
            Acesse o conteúdo mais quente do Rio de Janeiro. 
            As cariocas mais gostosas em vídeos exclusivos que você não encontra em outro lugar.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              data-testid="hero-premium-btn"
              onClick={() => openWhatsApp("Quero assinar o plano PREMIUM por R$21,99/mês! 🔥")}
              className="px-8 py-6 text-lg font-bold uppercase tracking-wide bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#b38728] text-black hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-full neon-gold"
            >
              <Crown className="w-5 h-5 mr-2" />
              Assinar Premium - R$21,99
            </Button>

            <Button
              data-testid="hero-free-btn"
              onClick={() => openWhatsApp("Quero acessar o conteúdo GRATUITO! 🎬")}
              variant="outline"
              className="px-8 py-6 text-lg font-bold uppercase tracking-wide border-[#FF0033] text-[#FF0033] hover:bg-[#FF0033]/10 hover:scale-105 transition-all duration-300 rounded-full"
            >
              <Play className="w-5 h-5 mr-2" />
              Testar Grátis
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.button
          data-testid="scroll-indicator"
          onClick={scrollToContent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ delay: 2, y: { repeat: Infinity, duration: 1.5 } }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[#D4AF37]"
        >
          <ChevronDown className="w-10 h-10" />
        </motion.button>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: Flame,
      title: "Conteúdo Quente",
      description: "Vídeos exclusivos das cariocas mais gostosas do Rio de Janeiro",
      color: "#FF0033"
    },
    {
      icon: Zap,
      title: "Atualizações Semanais",
      description: "Novo conteúdo toda semana para manter a chama acesa",
      color: "#D4AF37"
    },
    {
      icon: Crown,
      title: "Acesso VIP",
      description: "Conteúdos especiais e bastidores apenas para membros premium",
      color: "#00f0ff"
    },
    {
      icon: Star,
      title: "Qualidade HD",
      description: "Vídeos em alta definição para uma experiência imersiva",
      color: "#D4AF37"
    }
  ];

  return (
    <section data-testid="features-section" className="py-24 px-4 bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 
            className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            Por que escolher o{" "}
            <span className="gold-gradient-text">Rocco Sifredi?</span>
          </h2>
          <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
            A plataforma definitiva para quem busca conteúdo adulto carioca de verdade
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#0a0a0a]/80 backdrop-blur-md border border-white/5 rounded-2xl p-6 hover:border-[#D4AF37]/30 transition-all duration-300 group"
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${feature.color}20` }}
              >
                <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
              </div>
              <h3 
                className="text-xl font-bold mb-2 text-white"
                style={{ fontFamily: "'Unbounded', sans-serif" }}
              >
                {feature.title}
              </h3>
              <p className="text-[#a1a1aa] text-sm">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Content Preview Section
const ContentPreview = () => {
  return (
    <section id="content-section" data-testid="content-section" className="py-24 px-4 bg-gradient-to-b from-[#050505] to-[#0a0a0a]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 
            className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            <span className="text-[#FF0033]">Preview</span> do Conteúdo
          </h2>
          <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
            Uma amostra do que te espera dentro da plataforma
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {PREVIEW_CONTENT.map((content, index) => (
            <motion.div
              key={content.id}
              data-testid={`content-preview-${content.id}`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              onClick={() => openWhatsApp(content.premium 
                ? `Quero assistir "${content.title}" - Acesso Premium! 🔥` 
                : `Quero ver o vídeo "${content.title}"! 🎬`
              )}
              className="relative aspect-[9/16] rounded-xl overflow-hidden cursor-pointer group"
            >
              {/* Placeholder Background */}
              <div 
                className={`absolute inset-0 bg-gradient-to-br from-[#FF0033]/30 to-[#D4AF37]/20 ${content.premium ? 'blur-lock' : ''}`}
                style={{
                  backgroundImage: `url(https://images.unsplash.com/photo-1713284060723-5be78613225f?w=400)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-all duration-300" />

              {/* Premium Lock */}
              {content.premium && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <div className="bg-black/70 backdrop-blur-sm rounded-full p-3">
                    <Lock className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                </div>
              )}

              {/* Content Info */}
              <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                <div className="flex items-center gap-2 mb-1">
                  {content.premium ? (
                    <span className="px-2 py-0.5 bg-[#D4AF37] text-black text-xs font-bold rounded-full">
                      PREMIUM
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-[#22c55e] text-white text-xs font-bold rounded-full">
                      FREE
                    </span>
                  )}
                </div>
                <h3 className="text-white text-sm font-bold truncate">{content.title}</h3>
                <p className="text-[#a1a1aa] text-xs">{content.duration}</p>
              </div>

              {/* Play Button on Hover */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-16 h-16 rounded-full bg-[#FF0033]/90 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white fill-white ml-1" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button
            data-testid="view-all-content-btn"
            onClick={() => openWhatsApp("Quero ver todo o catálogo de conteúdo! 🔥")}
            className="px-8 py-6 text-lg font-bold uppercase tracking-wide bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#b38728] text-black hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-full"
          >
            Ver Todo o Catálogo
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const plans = [
    {
      name: "Free",
      price: "Grátis",
      period: "para sempre",
      description: "Experimente antes de assinar",
      features: [
        { text: "5 vídeos gratuitos", included: true },
        { text: "Qualidade padrão", included: true },
        { text: "Suporte básico", included: true },
        { text: "Conteúdo exclusivo", included: false },
        { text: "Sem anúncios", included: false },
        { text: "Acesso VIP", included: false },
      ],
      cta: "Começar Grátis",
      message: "Quero criar minha conta GRATUITA! 🎬",
      highlighted: false
    },
    {
      name: "Premium",
      price: "R$21,99",
      period: "/mês",
      description: "Acesso ilimitado a todo conteúdo",
      features: [
        { text: "Vídeos ilimitados", included: true },
        { text: "Qualidade Full HD", included: true },
        { text: "Suporte prioritário", included: true },
        { text: "Conteúdo exclusivo", included: true },
        { text: "Sem anúncios", included: true },
        { text: "Acesso VIP e bastidores", included: true },
      ],
      cta: "Assinar Agora",
      message: "Quero assinar o plano PREMIUM por R$21,99/mês! 🔥👑",
      highlighted: true
    }
  ];

  return (
    <section data-testid="pricing-section" className="py-24 px-4 bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 
            className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            Escolha seu <span className="gold-gradient-text">Plano</span>
          </h2>
          <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
            Comece grátis ou desbloqueie tudo com o Premium
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className={`relative rounded-2xl p-8 ${
                plan.highlighted 
                  ? 'bg-gradient-to-b from-black to-zinc-900 border-2 border-[#D4AF37] animated-border' 
                  : 'bg-zinc-900/50 border border-zinc-800'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#b38728] text-black text-sm font-bold rounded-full uppercase">
                    Recomendado
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 
                  className={`text-2xl font-bold mb-2 ${plan.highlighted ? 'gold-gradient-text' : 'text-gray-400'}`}
                  style={{ fontFamily: "'Unbounded', sans-serif" }}
                >
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-4xl font-black ${plan.highlighted ? 'text-white' : 'text-gray-400'}`}>
                    {plan.price}
                  </span>
                  <span className="text-[#a1a1aa] text-sm">{plan.period}</span>
                </div>
                <p className="text-[#a1a1aa] text-sm mt-2">{plan.description}</p>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    {feature.included ? (
                      <div className="w-5 h-5 rounded-full bg-[#22c55e]/20 flex items-center justify-center">
                        <Check className="w-3 h-3 text-[#22c55e]" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center">
                        <X className="w-3 h-3 text-zinc-600" />
                      </div>
                    )}
                    <span className={feature.included ? 'text-white' : 'text-zinc-600'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                onClick={() => openWhatsApp(plan.message)}
                className={`w-full py-6 text-lg font-bold uppercase tracking-wide rounded-full transition-all duration-300 ${
                  plan.highlighted 
                    ? 'bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#b38728] text-black hover:brightness-110 hover:scale-105' 
                    : 'bg-zinc-800 text-white hover:bg-zinc-700'
                }`}
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 px-4 bg-[#0a0a0a] border-t border-white/5">
      <div className="max-w-4xl mx-auto text-center">
        <motion.img
          src={LOGO_URL}
          alt="Rocco Sifredi"
          className="w-48 mx-auto mb-6 opacity-80"
        />

        <div className="flex justify-center gap-6 mb-8">
          <motion.a
            data-testid="footer-instagram-link"
            href={`https://instagram.com/${INSTAGRAM_HANDLE}`}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1 }}
            className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 transition-all duration-300"
          >
            <Instagram className="w-5 h-5 text-white" />
          </motion.a>

          <motion.button
            data-testid="footer-whatsapp-link"
            onClick={() => openWhatsApp("Olá! Vim pelo site e quero mais informações!")}
            whileHover={{ scale: 1.1 }}
            className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-[#22c55e] transition-all duration-300"
          >
            <MessageCircle className="w-5 h-5 text-white" />
          </motion.button>
        </div>

        <p className="text-[#a1a1aa] text-sm mb-2">
          Conteúdo destinado exclusivamente para maiores de 18 anos.
        </p>
        <p className="text-zinc-600 text-xs">
          © {new Date().getFullYear()} Rocco Sifredi. Todos os direitos reservados.
        </p>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="grain-overlay">
      <AudioPlayer />
      <WhatsAppButton />
      <HeroSection />
      <FeaturesSection />
      <ContentPreview />
      <PricingSection />
      <Footer />
    </div>
  );
}

export default App;
